import { createDraftSafeSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { GlobalState } from '@store'
import { LoadingState, User } from '@types'

export interface CommissionDeduction {
  personId: string
  name: string
  createdAt?: string
  stages?: {
    office?: string
    rejected?: string
    fee?: number
  }
  prepayment: {
    status?: string
    amount?: number
  }
  deductionAmount: number
  sourceInvoiceId?: string
}

export interface CommissionTotals {
  gross: number
  deductions: number
  net: number
}

export interface CommissionSummaryResponse {
  commissions: User[]
  deductions: CommissionDeduction[]
  totals: CommissionTotals
}

export type CommissionComparisonStatus = 'no_draft' | 'up_to_date' | 'additions_only' | 'invalidating_changes'

export interface CommissionInvoiceLine {
  lineKey?: string
  personId: string
  kind?: 'commission' | 'deduction'
  name: string
  amount?: number
  prepaymentAmount?: number | null
  percentage?: number | null
  promotionAmount?: number | null
  promotionPercentage?: number | null
  isDeduction?: boolean
}

export interface CommissionDraft {
  _id: string
  serial: string
  serialNumber: number
  status: string
  amount: number
  commissions: CommissionInvoiceLine[]
  createdAt?: string
}

export interface CommissionDraftContext {
  activeDraft: CommissionDraft | null
  currentClaim: {
    amount: number
    totals: CommissionTotals
    lines: CommissionInvoiceLine[]
  }
  comparison: {
    status: CommissionComparisonStatus
    fingerprint: string
    unchanged: CommissionInvoiceLine[]
    added: CommissionInvoiceLine[]
    modified: {
      reserved: CommissionInvoiceLine
      current: CommissionInvoiceLine
    }[]
    removed: CommissionInvoiceLine[]
  }
  acknowledgement: {
    decision: 'keep' | 'replace'
    acknowledgedAt: string
    acknowledgedDay: string
    comparisonFingerprint: string
    validForToday: boolean
  } | null
  timezone: 'America/Bogota'
}

interface commissionsState {
  loadingStatus: LoadingState
  message: string
  commissions: User[]
  deductions: CommissionDeduction[]
  totals: CommissionTotals
  draftContext: CommissionDraftContext | null
}

export interface Invoice {
  invoiceId?: string
  userId?: string
  commissions: {
    personId: string
    amount?: number
    prepaymentAmount?: number
    percentage?: number
    promotionAmount?: number
    promotionPercentage?: number
    name: string
  }[]
  deductions?: {
    personId: string
    amount: number
    name: string
  }[]
  amount: number
  status?: string
  file?: string
  serial?: string
  type?: string
}

const EMPTY_TOTALS: CommissionTotals = {
  gross: 0,
  deductions: 0,
  net: 0
}

const normalizeTotals = (totals?: Partial<CommissionTotals>): CommissionTotals => ({
  gross: Number(totals?.gross) || 0,
  deductions: Number(totals?.deductions) || 0,
  net: Number(totals?.net) || 0
})

const normalizeCommissionSummaryResponse = (payload: CommissionSummaryResponse | User[]): CommissionSummaryResponse => {
  if (Array.isArray(payload)) {
    return {
      commissions: payload,
      deductions: [],
      totals: EMPTY_TOTALS
    }
  }

  return {
    commissions: Array.isArray(payload?.commissions) ? payload.commissions : [],
    deductions: Array.isArray(payload?.deductions) ? payload.deductions : [],
    totals: normalizeTotals(payload?.totals)
  }
}

const initialState: commissionsState = {
  loadingStatus: 'NOT_STARTED',
  message: '',
  commissions: [],
  deductions: [],
  totals: EMPTY_TOTALS,
  draftContext: null
}

const commissionsSlice = createSlice({
  name: 'commissions',
  initialState,

  reducers: {
    getCommissions: state => {
      return {
        ...state,
        loadingStatus: 'LOADING',
        message: ''
      }
    },
    postInvoice: (state, _action: PayloadAction<Invoice>) => {
      return {
        ...state,
        loadingStatus: 'LOADING',
        message: ''
      }
    },
    postInvoiceSuccess: state => {
      return {
        ...state,
        loadingStatus: 'SUCCESS',
        message: ''
      }
    },
    getCommissionsSuccess: (state, action: PayloadAction<CommissionSummaryResponse | User[]>) => {
      const summary = normalizeCommissionSummaryResponse(action.payload)
      return {
        ...state,
        loadingStatus: 'SUCCESS',
        message: '',
        commissions: summary.commissions,
        deductions: summary.deductions,
        totals: summary.totals
      }
    },
    getCommissionContextSuccess: (state, action: PayloadAction<CommissionDraftContext>) => {
      state.draftContext = action.payload
    },
    getCommissionsFailed: (state, action) => {
      return {
        ...state,
        loadingStatus: 'FAILED',
        message: action.payload
      }
    },
    clearCommissionsMessage: state => {
      return {
        ...state,
        message: ''
      }
    },
    clearCommissions: _state => {
      return initialState
    }
  }
})

export const commissionsLoader = createDraftSafeSelector(
  [(state: GlobalState) => state.commissions.loadingStatus, (state: GlobalState) => state.commissions.message],
  (loadingStatus, message) => {
    const isLoading = loadingStatus === 'LOADING'
    const isFailed = loadingStatus === 'FAILED'
    const isSuccess = loadingStatus === 'SUCCESS'
    return { isLoading, isFailed, isSuccess, message }
  }
)

export const selectCommissions = createDraftSafeSelector(
  [(state: GlobalState) => state.commissions.commissions],
  commissions => commissions || []
)

export const selectDeductions = createDraftSafeSelector(
  [(state: GlobalState) => state.commissions.deductions],
  deductions => deductions || []
)

export const selectCommissionTotals = createDraftSafeSelector(
  [(state: GlobalState) => state.commissions.totals],
  totals => totals || EMPTY_TOTALS
)

export const selectCommissionDraftContext = createDraftSafeSelector(
  [(state: GlobalState) => state.commissions.draftContext],
  draftContext => draftContext
)

export const {
  getCommissions,
  getCommissionsFailed,
  getCommissionsSuccess,
  getCommissionContextSuccess,
  postInvoiceSuccess,
  postInvoice,
  clearCommissionsMessage
} = commissionsSlice.actions
export default commissionsSlice.reducer
